import type { FC, PropsWithChildren } from "react";
import { NavLink, useHref, type To } from "react-router";
import { tv } from "tailwind-variants";

// Legacy tab strip still used inside slideovers (agent result / grading
// overviews). Page-level tabs use `Tabs`/`RouteTabs` with `PageHeader`
// instead.

//
// Tabs component.
//

interface TabsLayoutTabsProps extends PropsWithChildren {
  isLoading: boolean;
}

export const TabsLayoutTabs: FC<TabsLayoutTabsProps> = (props) => {
  const { isLoading, children } = props;

  if (isLoading) {
    return null;
  }

  return (
    <div
      role="tablist"
      className="flex min-h-[50px] shrink-0 gap-3 self-stretch"
    >
      {children}
    </div>
  );
};

//
// Tab component.
//

interface TabsLayoutTabProps extends PropsWithChildren {
  to: To;
  end?: boolean;
}

const tabStyle = tv({
  base: `
    flex
    items-center
    border-b-2
    border-transparent
    px-1
    text-sm
    font-semibold
    text-gray-700
    hover:border-gray-500
    hover:text-foreground
  `,
  variants: {
    isActive: {
      true: `
        border-indigo-500
        text-indigo-600
        hover:border-indigo-500
        hover:text-indigo-600
      `,
    },
  },
});

export const TabsLayoutTab: FC<TabsLayoutTabProps> = (props) => {
  const { to, end = true, children } = props;
  const fullTo = useHref(to);
  return (
    <NavLink
      role="tab"
      to={fullTo}
      // `end` defaults to exact matching; callers can opt into nested-route matching for tab subroutes.
      end={end}
      className={({ isActive }) => tabStyle({ isActive })}
    >
      {children}
    </NavLink>
  );
};
